import { Flex, Center, Button } from '@chakra-ui/react';
import { decode } from 'js-base64';
import { Code02 } from '@kula-ai/icons-core';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationCard } from '../../components/Card';
import { NotificationTime } from '../../components/Time';
import {
  CollapsibleSection,
  DetailsList,
} from '../referral-notifications/components';
import { KText } from '@/ui-library/components';
import type { NotificationItem } from '../NotificationList.interface';

interface DecodedCustomAttributes {
  token_name?: string;
  token_management_url?: string;
  expires_in_days?: number | string;
}

interface PatNotificationProps {
  notification?: NotificationItem;
  onClose?: () => void;
}

type PatVariant = 'about_to_expire' | 'expired';

const PatExpiry = ({
  notification,
  onClose = () => {},
  variant,
}: PatNotificationProps & { variant: PatVariant }) => {
  const [markAsRead] = useMarkAsReadMutation();

  const parseAttributes = (): DecodedCustomAttributes => {
    try {
      return JSON.parse(decode(notification?.custom_attributes ?? ''));
    } catch (error) {
      console.error('Error decoding PAT expiry notification:', error);
      return {};
    }
  };
  const { token_name, token_management_url, expires_in_days } =
    parseAttributes();

  const isExpired = variant === 'expired';

  const details = [
    { label: 'PAT', value: token_name },
    ...(isExpired
      ? []
      : [{ label: 'Expires', value: `${expires_in_days} days` }]),
  ];

  const handleGoToPatSettings = () => {
    if (token_management_url) {
      markAsRead({ id: notification?.id });
      window.open(token_management_url, '_self');
      onClose();
    }
  };

  return (
    <Flex alignItems="baseline" gap="6px">
      <Center minW="34px" h="34px" borderRadius="sm" bg="primary-100">
        <Code02
          size={16}
          color="var(--kula-colors-primary-500)"
          className="kula-icon"
        />
      </Center>
      <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" flex="1">
        <KText fontWeight="semiBold">
          Your {token_name} {isExpired ? 'has expired' : 'is about to expire'}
        </KText>

        <CollapsibleSection title="Details">
          <DetailsList data={details} />
        </CollapsibleSection>

        <Button
          variant="primary"
          onClick={handleGoToPatSettings}
          size="sm"
          w="fit-content"
          data-test-id="go-to-pat-settings-button"
        >
          Go to PAT settings
        </Button>

        <NotificationTime notification={notification} />
      </Flex>
    </Flex>
  );
};

export const PatAboutToExpireNotification = ({
  notification,
  onClose = () => {},
}: PatNotificationProps) => (
  <NotificationCard notification={notification}>
    <PatExpiry
      notification={notification}
      onClose={onClose}
      variant="about_to_expire"
    />
  </NotificationCard>
);

export const PatExpiredNotification = ({
  notification,
  onClose = () => {},
}: PatNotificationProps) => (
  <NotificationCard notification={notification}>
    <PatExpiry
      notification={notification}
      onClose={onClose}
      variant="expired"
    />
  </NotificationCard>
);
